<script setup lang="ts">
  import { ref, onMounted, onBeforeUnmount } from 'vue'
  import { KleosOauthService, KleosService } from '@/types/generated/services.gen'
  import { ApiError } from '@/types/generated/core/ApiError'
  import { useTranslation } from '@/composables/useTranslation'
  import { useKleosStore } from '@/shared/stores/kleosStore'

  const props = defineProps<{
    onSuccess?: () => void
    onClose?: () => void
  }>()

  const emit = defineEmits(['success', 'close'])

  // Initialize Kleos store
  const kleosStore = useKleosStore()

  const isLoading = ref(true)
  const isAuthenticated = ref(false)
  const errorMessage = ref('')
  const countdown = ref(3)
  const countdownInterval = ref<number | null>(null)

  const { t } = useTranslation({
    en: {
      title: 'Kleos Authentication',
      authenticating: 'Authenticating with Kleos...',
      authSuccess: 'Successfully authenticated with Kleos!',
      authFailed: 'Authentication with Kleos failed.',
      redirecting: 'Redirecting to Kleos authentication page...',
      closeIn: 'This window will close in {seconds} seconds',
      close: 'Close',
      retry: 'Retry'
    },
    de: {
      title: 'Kleos-Authentifizierung',
      authenticating: 'Authentifizierung mit Kleos...',
      authSuccess: 'Erfolgreich bei Kleos authentifiziert!',
      authFailed: 'Authentifizierung bei Kleos fehlgeschlagen.',
      redirecting: 'Weiterleitung zur Kleos-Authentifizierungsseite...',
      closeIn: 'Dieses Fenster schließt in {seconds} Sekunden',
      close: 'Schließen',
      retry: 'Wiederholen'
    }
  })

  // Check if Kleos token is valid by pinging the API
  const checkKleosTokenValidity = async () => {
    try {
      isLoading.value = true
      errorMessage.value = ''

      // Try to fetch Kleos case types to check if token is valid
      await KleosService.kleosCaseTypesRetrieve()

      // If we get here, authentication is successful
      isAuthenticated.value = true
      isLoading.value = false

      // Start countdown to close
      startCountdown()

      // Emit success event
      emit('success')
      if (props.onSuccess) props.onSuccess()

      // set the successful state of the authentication on the pinia store
      kleosStore.setSuccessfulAuthenticationState()

      return true
    } catch (error) {
      isLoading.value = false

      if (error instanceof ApiError) {
        // If we get a 401 or 403, we need to redirect to Kleos auth
        if (error.status === 401 || error.status === 403) {
          redirectToKleosAuth()
          return false
        }

        // Other API errors
        errorMessage.value = error.message || t('authFailed')
      } else {
        // Generic error
        errorMessage.value = t('authFailed')
        console.error('Kleos authentication error:', error)
      }

      return false
    }
  }

  // Redirect to Kleos OAuth authorization endpoint
  const redirectToKleosAuth = async () => {
    try {
      const response = await KleosOauthService.kleosOauthAuthorizeRetrieve()

      // The API returns a redirect URL in the response
      if (response && response.redirect_url) {
        window.location.href = response.redirect_url
      } else {
        // Fallback to the standard endpoint if no redirect URL is provided
        window.location.href = '/api/v3/kleos_oauth/authorize/'
      }
    } catch (error) {
      console.error('Failed to get Kleos auth URL:', error)
      errorMessage.value = t('authFailed')
      isLoading.value = false
    }
  }

  // Start countdown to close the popup
  const startCountdown = () => {
    countdown.value = 3

    countdownInterval.value = window.setInterval(() => {
      countdown.value--

      if (countdown.value <= 0) {
        clearInterval(countdownInterval.value!)
        closePopup()
      }
    }, 1000)
  }

  // Close the popup
  const closePopup = () => {
    emit('close')
    if (props.onClose) props.onClose()

    // close the window
    window.close()
  }

  // Retry authentication
  const retryAuthentication = () => {
    checkKleosTokenValidity()
  }

  // Clean up interval on component unmount
  onBeforeUnmount(() => {
    if (countdownInterval.value) {
      clearInterval(countdownInterval.value)
    }
  })

  // Check authentication status when component mounts
  onMounted(() => {
    checkKleosTokenValidity()
  })
</script>

<template>
  <div class="kleos-auth-popup mx-auto max-w-md rounded-xl bg-white p-6 shadow-md">
    <h2 class="mb-4 text-xl font-bold">{{ t('title') }}</h2>

    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="flex flex-col items-center py-4"
    >
      <div class="mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-purple-600"></div>
      <p>{{ t('authenticating') }}</p>
    </div>

    <!-- Success state -->
    <div
      v-else-if="isAuthenticated"
      class="flex flex-col items-center py-4"
    >
      <div class="mb-4 w-full rounded-md bg-green-100 p-4 text-green-700">
        <p class="font-medium">{{ t('authSuccess') }}</p>
        <p class="mt-2">{{ t('closeIn', { seconds: countdown }) }}</p>
      </div>

      <button
        class="mt-4 rounded bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700"
        @click="closePopup"
      >
        {{ t('close') }}
      </button>
    </div>

    <!-- Error state -->
    <div
      v-else-if="errorMessage"
      class="flex flex-col items-center py-4"
    >
      <div class="mb-4 w-full rounded-md bg-red-100 p-4 text-red-700">
        <p class="font-medium">{{ errorMessage }}</p>
      </div>

      <div class="flex space-x-4">
        <button
          class="mt-4 rounded bg-gray-200 px-4 py-2 text-gray-800 transition-colors hover:bg-gray-300"
          @click="closePopup"
        >
          {{ t('close') }}
        </button>

        <button
          class="mt-4 rounded bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700"
          @click="retryAuthentication"
        >
          {{ t('retry') }}
        </button>
      </div>
    </div>

    <!-- Redirect state (fallback) -->
    <div
      v-else
      class="flex flex-col items-center py-4"
    >
      <p>{{ t('redirecting') }}</p>
    </div>
  </div>
</template>

<style scoped>
  .kleos-auth-popup {
    min-height: 250px;
    display: flex;
    flex-direction: column;
  }
</style>
